import type { CSSProperties, FC, ReactNode } from 'react'

import {
    Fragment,
    useEffect,
    useMemo,
    useRef
} from 'react'

import { cn } from '@/lib/utils'

interface ScrollLitTextProps {
    text: string
    highlights?: ReadonlyArray<string>
    className?: string
}

const RAMP = 8

/**
 * A paragraph whose words light up one by one as it scrolls through the viewport.
 * Only a single CSS variable changes on scroll, each word derives its own opacity from it.
 */
const ScrollLitText: FC<ScrollLitTextProps> = ({ text, highlights = [], className }): ReactNode => {
    const ref = useRef<HTMLParagraphElement>(null)
    const words = useMemo(() => text.split(' '), [text])
    const marked = useMemo(() => new Set(highlights.map(word => word.toLowerCase())), [highlights])

    useEffect(() => {
        const node = ref.current

        if (!node) return

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            node.style.setProperty('--lit', '1')
            return
        }

        let frame = 0

        const update = () => {
            frame = 0

            const rect = node.getBoundingClientRect()
            const viewport = window.innerHeight
            const start = viewport * 0.9
            const end = viewport * 0.4
            const progress = (start - rect.top) / (rect.height + start - end)

            node.style.setProperty('--lit', Math.min(1, Math.max(0, progress)).toFixed(3))
        }

        const onScroll = () => {
            if (!frame) frame = window.requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', onScroll, { passive: true })
        window.addEventListener('resize', onScroll)

        return () => {
            window.removeEventListener('scroll', onScroll)
            window.removeEventListener('resize', onScroll)
            window.cancelAnimationFrame(frame)
        }
    }, [])

    return (
        <p
            ref={ref}
            className={className}
            style={{ '--lit': 0, '--count': words.length + RAMP } as CSSProperties}
        >
            {words.map((word, index) => {
                const clean = word.toLowerCase().replace(/[^a-z]/g, '')

                return (
                    <Fragment key={index}>
                        <span
                            className={cn(
                                'transition-opacity duration-150',
                                marked.has(clean) && 'bg-bubblegum px-1 [box-decoration-break:clone]'
                            )}
                            style={{ opacity: `clamp(0.16, calc((var(--lit) * var(--count) - ${index}) / ${RAMP}), 1)` }}
                        >
                            {word}
                        </span>
                        {' '}
                    </Fragment>
                )
            })}
        </p>
    )
}

export default ScrollLitText
